import { getCodeRepositoryName } from "@/domain/image"
import {
  defaultSortResourcesByType,
  getImageNameWithRegistry
} from "@/domain/resource"
import { getIssuesCountMapOrderedSeverity } from "@/domain/vulnerability"
import { formatVulnerabilitiesBySeverity } from "@/domain/vulnerability/vulnerability.formatters"
import {
  DATE_TIME_SHORT_FORMAT,
  getDateInFormat,
  getTimestampInMillisecondsFromSecond
} from "@/helpers/date.helpers"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { toDictionary } from "@/helpers/utils"
import { getBaseImageProperty } from "@/pages/DiscoveryPage/utils/inventory.utils"

import type {
  CodeRepoPackagesResponse,
  ImageResponse,
  PackageDescribeResponse,
  PackageVulnerabilitiesResponse
} from "@/api/useInventory.types"
import type {
  IssueCodeRepoResponse,
  IssueImageResponse
} from "@/api/useIssues.types"
import type { DataNode } from "@/atoms"
import type { ConfigVM, FlatNode, Path, TreeState } from "./useInventory.types"

export const getPackagesData = (response: CodeRepoPackagesResponse) => {
  return {
    metadata: response.metadata,
    data: response.data.map((item, index) => ({
      ...item,
      key: `${item.name}-${index}-${item.version}`,
      expandable: item.vulnerabilities.length > 0,
      vulnerabilitiesLength: item.vulnerabilities.length,
      rowData: item.vulnerabilities,
      vulnerabilities: formatVulnerabilitiesBySeverity(
        toDictionary(item.vulnerabilities, "cve")
      )
    }))
  }
}

export const getIssuesDataCodeRepository = (
  response: IssueCodeRepoResponse
) => {
  const { data, ...attr } = response || {}

  return {
    data: data.map((item, i: number) => {
      const {
        cve,
        cvss,
        name,
        paths,
        fixVersions,
        fixability,
        computationError,
        computedFixes = []
      } = item

      return {
        key: `${cve}_${name}_${i}`,
        packageName: name,
        expandable: true,
        rowData: [
          {
            key: `${cve}_${name}_${i}`,
            name,
            cve,
            cvss,
            fixVersions,
            computedFixes: computedFixes.sort((a, b) =>
              a.name.localeCompare(b.name)
            ),
            fixability,
            paths: (paths || [])
              .map((pathSet: { name: string; version: string }[]) =>
                (pathSet || [])
                  .map(({ name, version }) => `${name}:${version}`)
                  .join(" > ")
              )
              .sort((a, b) => a.localeCompare(b)),
            computationError
          }
        ],
        ...item
      }
    }),
    ...attr
  }
}

export const getImageTabData = (response: ImageResponse) => {
  const { data = [], metadata } = response || {}

  return {
    metadata,
    data: data.map(({ data: dataRecord, id, displayName }) => {
      const { applications = [] } = dataRecord
      const codeRepositories = dataRecord.codeRepositoriesData || []
      const baseImageNames = getBaseImageProperty(
        dataRecord.baseImagesData,
        "imageNames"
      )

      return {
        key: id,
        displayName,
        applicationsCount: {
          copyable: true,
          title: "Applications",
          items: (applications || []).map(
            ({ name, environment, namespace }) => ({
              name,
              description: `${environment || "-"}/${namespace || "-"}`
            })
          ),
          label: applications?.length || ""
        },
        ...dataRecord,
        codeRepositoriesDisplayValue: {
          copyable: true,
          items: codeRepositories,
          count: codeRepositories.length - 1,
          label: getCodeRepositoryName(codeRepositories[0]?.name)
        },
        baseImageName: {
          copyable: true,
          count: baseImageNames.length - 1,
          items: baseImageNames,
          label: baseImageNames[0] || "",
          width: "100%"
        },

        fullImageName: `${dataRecord.registry}/${dataRecord.repository}:${dataRecord.tag}`,
        fullDisplayName: getImageNameWithRegistry(
          dataRecord,
          dataRecord.repository
        ),
        digestShort: dataRecord.digest?.substring(7, 19),
        operatingSystems: capitalizeFirstLetter(dataRecord.os),
        firstSeen: getDateInFormat(
          getTimestampInMillisecondsFromSecond(
            parseInt(dataRecord.firstSeen, 10)
          ),
          DATE_TIME_SHORT_FORMAT
        )
      }
    })
  }
}

export const getIssuesDataImage = (response: IssueImageResponse) => {
  const { data, metadata } = response || {}

  return {
    metadata,
    data: data.map((issue, i) => {
      const { cve, name } = issue

      return {
        key: `${cve}_${name}_${i}`,
        rowIndex: i,
        packageName: name,
        expandable: true,
        rowData: [issue],
        ...issue
      }
    })
  }
}

export const getPackageVulnerabilities = (
  response: PackageVulnerabilitiesResponse
) => {
  const { data, ...rest } = response || {}

  return {
    data: data.map(({ fixVersions, ...vulnerability }) => ({
      key: vulnerability.id,
      fix: fixVersions.join(", "),
      ...vulnerability
    })),
    ...rest
  }
}

export const getIntroducedThrough = (type: string, introducedThrough: any) => {
  if (type === "ImageRepository")
    return Array.from(
      new Set(
        introducedThrough.flatMap(
          ({ packageLocations }: any) => packageLocations
        )
      )
    )

  if (type === "CodeRepository") return [introducedThrough]

  return introducedThrough
}

export const getIntroducedThroughResources = (
  introducedThroughResources: PackageDescribeResponse["data"]["data"]["introducedThroughResources"]
) => {
  const codeRepositories = introducedThroughResources.filter(
    ({ resource }) => resource.type === "CodeRepository"
  )

  if (codeRepositories.length === 0) return introducedThroughResources

  const introducedThrough = codeRepositories.map(
    ({ sourceId, introducedThrough }) => ({
      name: sourceId,
      paths: introducedThrough
    })
  )

  return [
    { resource: codeRepositories[0].resource, introducedThrough },
    ...introducedThroughResources.filter(
      ({ resource }) => resource.type !== "CodeRepository"
    )
  ]
}

export const getDescribe = (response: PackageDescribeResponse) => {
  const { data, ...rest } = response || {}
  const item = data.data
  const { resources, ...record } = item
  const { insights, introducedThroughResources } = record
  const { fromBaseImage, fromThirdParty } = insights

  const arr = introducedThroughResources.map(
    ({ resource, sourceId, introducedThrough, importPaths, ...rest }) => {
      const { type, ...restResource } = resource
      const emptyIntroducedThrough = !introducedThrough?.length
      const isCodeRepository = type === "CodeRepository"
      const showUndeterminedIntroducedThrough =
        isCodeRepository && emptyIntroducedThrough

      return {
        resource: {
          type: type === "ImageRepository" ? "Image" : type,
          baseImageDetailsExist: introducedThrough.some(
            (item) => !!item.baseImageDetails
          ),
          ...restResource
        },
        source: sourceId,
        introducedThrough: showUndeterminedIntroducedThrough
          ? [
              [
                {
                  name: "Unable to determine dependency hierarchy",
                  noDependencyHierarchy: true
                }
              ]
            ]
          : introducedThrough,
        importPaths: !importPaths?.length
          ? [[{ name: item.name, version: item.version }]]
          : importPaths,
        ...rest
      }
    }
  )

  const codeRepoFindings = arr.filter(
    (item) => item.resource.type === "CodeRepository"
  )
  // Third party takes priority over base image (a finding can have both insights true)
  const thirdPartyFindings = arr.filter(
    (item) => item.resource.type === "Image" && fromThirdParty
  )
  const baseImageFindings = arr.filter(
    (item) => item.resource.type === "Image" && fromBaseImage && !fromThirdParty
  )
  const imageFindings = arr.filter(
    (item) =>
      item.resource.type === "Image" && !fromBaseImage && !fromThirdParty
  )
  const vmFindings = arr.filter(
    (item) => item.resource.type === "Vm" && !fromBaseImage
  )

  return {
    data: {
      codeRepoFindings,
      baseImageFindings,
      thirdPartyFindings,
      imageFindings,
      vmFindings,
      issues: getIssuesCountMapOrderedSeverity(record.vulnerabilitiesSummary),
      resources: defaultSortResourcesByType(resources),
      ...record
    },
    ...rest
  }
}

export const splitPackageIdFromDrawerRecord = (props: {
  record?: { id: string }
}) => {
  const [projectId, id] = props.record?.id.split("-") || []

  return { projectId, id }
}

export const getArtifactSources = (
  artifactSources: string[],
  state: { childrenMap: { root?: string[] } }
) =>
  artifactSources.length === 0
    ? (state.childrenMap.root ?? [])
    : artifactSources

export const buildState = (data: Path[], cfg: ConfigVM[]): TreeState => {
  const nodes: Record<string, FlatNode> = {}
  const childrenMap: Record<string, string[]> = { root: [] }

  const ensureNode = (
    key: string,
    title: string,
    parentKey: string,
    isLeaf: boolean
  ) => {
    if (nodes[key]) return

    nodes[key] = { key, title, parentKey, isLeaf }
    ;(childrenMap[parentKey] ??= []).push(key)

    if (!isLeaf) childrenMap[key] ??= []
  }

  for (const { path, files, stale } of data) {
    const segments = path.split("/").filter(Boolean)
    let parentKey = "root"
    let currentKey = ""

    for (const [index, segment] of segments.entries()) {
      currentKey +=
        index === 0 && path.startsWith(segment) ? segment : "/" + segment
      ensureNode(currentKey, segment, parentKey, false)
      parentKey = currentKey
    }

    if (stale && currentKey) nodes[currentKey] = { ...nodes[currentKey], stale }

    for (const file of files) {
      ensureNode(`${path}/${file.name}`, file.name, path, true)
    }
  }

  const nodeKeys = Object.keys(nodes)

  cfg.forEach(({ artifactSources }) => {
    getArtifactSources(artifactSources, { childrenMap }).forEach((path) => {
      if (nodes[path])
        nodeKeys.forEach((key) => {
          if (
            (key === path || key.startsWith(`${path}/`)) &&
            !nodes[key].isLeaf
          )
            nodes[key] = { correlated: true, ...nodes[key] }
        })
    })
  })

  return { nodes, childrenMap }
}

export const buildTreeData = (
  state: TreeState,
  parentKey = "root"
): DataNode[] => {
  return (state.childrenMap[parentKey] ?? [])
    .map((key) => state.nodes[key])
    .sort(
      (a, b) =>
        Number(a.isLeaf) - Number(b.isLeaf) || a.title.localeCompare(b.title)
    )
    .map((node) => ({
      key: node.key,
      title: node.title,
      isLeaf: node.isLeaf,
      checkable: !node.isLeaf,
      children: node.isLeaf ? undefined : buildTreeData(state, node.key)
    }))
}

export const countSelectedPaths = (
  state: TreeState,
  selectedKeys: string[]
): { selected: number; total: number; correlated: number } => {
  const countDescendants = (key: string): number => {
    const node = state.nodes[key]

    if (!node || node.isLeaf) return 0

    return (state.childrenMap[key] ?? []).reduce(
      (acc, childKey) => acc + countDescendants(childKey),
      1
    )
  }

  const rootKeys = selectedKeys.filter((key) => {
    let parentKey = state.nodes[key]?.parentKey

    while (parentKey && parentKey !== "root") {
      if (selectedKeys.includes(parentKey)) return false

      parentKey = state.nodes[parentKey]?.parentKey
    }

    return true
  })

  const isRoot =
    rootKeys.length === 1 &&
    (rootKeys[0] === "root" || !state.nodes[rootKeys[0]])

  const total = (state.childrenMap["root"] ?? []).reduce(
    (acc, key) => acc + countDescendants(key),
    0
  )

  const correlated = Object.values(state.nodes).filter(
    (node) => !node.isLeaf && node.correlated
  ).length

  return {
    selected: isRoot
      ? total
      : rootKeys.reduce((acc, key) => acc + countDescendants(key), 0),
    total,
    correlated
  }
}
